/**
 * PRD.330 Phase 5 follow-up — category-bar ⇄ canonical column filter.
 *
 * A chart category click is an explicit way to set the EXISTING values
 * filter on the visualization's dimension column — never a chart-only
 * filter. Selection shown in the chart is read back from the applied
 * `ReportQuery`, so a filter applied from the column popover (including
 * several values) is reflected identically. Pure and React-free.
 */
import type { ReportColumnFilter, ReportQuery } from '@helios/domain';

/** Values currently selected for `columnId` in the APPLIED query ([] = no filter). */
export function selectedValuesFor<C extends string>(query: ReportQuery<C>, columnId: C): string[] {
  const filter = query.filters[columnId];
  return filter?.kind === 'values' ? [...filter.values] : [];
}

/**
 * Next filter after a category click:
 *   - the only selected value clicked again → clear (undefined);
 *   - anything else → select exactly that one value (replaces any existing,
 *     including a multi-value selection).
 */
export function toggleCategoryFilter(
  current: ReportColumnFilter | undefined,
  value: string,
): ReportColumnFilter | undefined {
  if (current?.kind === 'values' && current.values.length === 1 && current.values[0] === value) {
    return undefined;
  }
  return { kind: 'values', values: [value] };
}

/** "Cash, GCash" — the persistent feedback lists the applied values by label. */
export function selectedValuesText(
  values: ReadonlyArray<string>,
  labelFor: (value: string) => string,
): string {
  return values.map(labelFor).join(', ');
}

/**
 * Escape clears the chart's filter only when there is one, and only when no
 * other handler (e.g. an open popover) has already handled the keystroke.
 */
export function shouldClearOnEscape(
  event: { key: string; defaultPrevented: boolean },
  selectedCount: number,
): boolean {
  return event.key === 'Escape' && !event.defaultPrevented && selectedCount > 0;
}

/** The two controller operations the binding needs (keeps it testable). */
export interface CategoryFilterController<C extends string> {
  query: ReportQuery<C>;
  setColumnFilter: (columnId: C, filter: ReportColumnFilter | undefined) => void;
  clearColumn: (columnId: C) => void;
}

/**
 * Wire a category-bar visualization to its dimension's canonical filter.
 * The chart component only reports "category activated" / "clear".
 */
export function categoryFilterBinding<C extends string>(
  controller: CategoryFilterController<C>,
  columnId: C,
): {
  selectedValues: string[];
  onCategoryActivate: (value: string) => void;
  onClearSelection: () => void;
} {
  return {
    selectedValues: selectedValuesFor(controller.query, columnId),
    onCategoryActivate: (value) =>
      controller.setColumnFilter(
        columnId,
        toggleCategoryFilter(controller.query.filters[columnId], value),
      ),
    onClearSelection: () => controller.clearColumn(columnId),
  };
}
